import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import { hasRoles, mergeRoles, CREATE_CLIENT_ROLES, EDIT_CLIENT_ROLES } from '../../../../util/RoleUtil';
import { validateSouthAfricanIdNumber } from '../../../../util/ValidationUtil';
import { DATE_PICKER_POPPER_MODIFIERS } from '../../../../util/DateUtil';

import { retrievePersonById } from '../../../../services/PersonService';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { ToolTip } from '../../../common/tool-tip/ToolTip';

const roles = mergeRoles(CREATE_CLIENT_ROLES, EDIT_CLIENT_ROLES);

export const ClientsCreateGeneralTab = ({
  client,
  setClient,
  setShowConsentTab,
  handleSaveClick,
  handleUpdate,
  context,
  setContext,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
  editMode,
}) => {
  const [title, setTitle] = useState(null);
  const [registrationDate, setRegistrationDate] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [whatShouldWeCallYou, setWhatShouldWeCallYou] = useState('');
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [saIdNumber, setSAIdNumber] = useState('');
  const [passportNumber, setPassportNumber] = useState('');

  const clientOriginsList = useRef(['Outreach Event', 'Walk-In', 'Pre-Screened', 'Screening']);
  const genderList = useRef(['Male', 'Female', 'Not Disclosed']);
  const titleList = useRef(['Mr', 'Ms', 'Mrs', 'Miss', 'Other']);

  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [selectedOriginError, setSelectedOriginError] = useState('');
  const [dateOfBirthError, setDateOfBirthError] = useState('');
  const [registrationDateError, setRegistrationDateError] = useState('');
  const [saIdNumberError, setSaIdNumberError] = useState('');
  const [passportNumberError, setPassportNumberError] = useState('');
  const [sexAtBirthError, setSexAtBirthError] = useState('');

  useEffect(() => {
    if (client && Object.keys(client).length !== 0) {
      setRegistrationDate(client.registrationDate ? new Date(client.registrationDate) : null);
      setFirstName(client.firstName || '');
      setLastName(client.lastName || '');
      setSelectedOrigin(
        client.origin
          ? typeof client.origin === 'object'
            ? { value: client.origin.value, label: client.origin.label }
            : { value: client.origin, label: client.origin }
          : null
      );
      setSelectedGender(
        client.sexAtBirth
          ? typeof client.sexAtBirth === 'object'
            ? { value: client.sexAtBirth.value, label: client.sexAtBirth.label }
            : { value: client.sexAtBirth, label: client.sexAtBirth }
          : null
      );
      setDateOfBirth(client.dob ? new Date(client.dob) : null);
      setSAIdNumber(client.saIdNumber || '');
      setPassportNumber(client.passportNumber || '');
      setWhatShouldWeCallYou(client.whatShouldWeCallYou || '');
      setTitle(
        client.title
          ? typeof client.title === 'object'
            ? { value: client.title.value, label: client.title.label }
            : { value: client.title, label: client.title }
          : null
      );
    }
  }, [client]);

  const handleOriginChange = (selectedOriginOption) => {
    setSelectedOrigin(selectedOriginOption);
    setSelectedOriginError(selectedOriginOption ? '' : '*REQUIRED');
  };

  const handleGenderChange = (selectedOption) => {
    setSelectedGender(selectedOption);
  };

  const validateFields = async () => {
    let isValid = true;
    const currentDate = new Date();

    if (!firstName) {
      setFirstNameError('*REQUIRED');
      isValid = false;
    } else {
      setFirstNameError('');
    }

    if (!lastName) {
      setLastNameError('*REQUIRED');
      isValid = false;
    } else {
      setLastNameError('');
    }

    if (!dateOfBirth) {
      setDateOfBirthError('*REQUIRED');
      isValid = false;
    } else if (dateOfBirth && dateOfBirth > currentDate) {
      setDateOfBirthError('*Cannot select a future date');
      isValid = false;
    } else if (dateOfBirth && registrationDate && dateOfBirth >= registrationDate) {
      setDateOfBirthError('*Date of Birth cannot be after Registration Date');
      isValid = false;
    } else {
      setDateOfBirthError('');
    }

    if (!registrationDate) {
      setRegistrationDateError('*REQUIRED');
      isValid = false;
    } else if (registrationDate && registrationDate > currentDate) {
      setRegistrationDateError('*Cannot select a future date');
      isValid = false;
    } else if (registrationDate && dateOfBirth && dateOfBirth >= registrationDate) {
      setRegistrationDateError('*Registration Date cannot be before Date of Birth');
      isValid = false;
    } else {
      setRegistrationDateError('');
    }

    if (!saIdNumber && !passportNumber) {
      setSaIdNumberError('*REQUIRED');
      setPassportNumberError('*REQUIRED');
      isValid = false;
    } else if (saIdNumber) {
      if (validateSouthAfricanIdNumber(saIdNumber)) {
        setPassportNumberError('');

        setSpinnerText('Checking ID Number');
        const [data, error] = await retrievePersonById(context.username, saIdNumber);

        if (data) {
          if (data.responseStatus === 'Success') {
            if (data.personVOToView) {
              if (context.additionalContext) {
                if (context.additionalContext.clientVO.saIdNumber === saIdNumber) {
                  setSaIdNumberError('');
                } else {
                  setSaIdNumberError('*DUPLICATE ID NUMBER');
                  isValid = false;
                }
              } else {
                setSaIdNumberError('*DUPLICATE ID NUMBER');
                isValid = false;
              }
            } else {
              setSaIdNumberError('');
            }
          } else {
            showToaster(data);
            isValid = false;
          }
          setSpinnerText(null);
        } else {
          showToaster({
            responseStatus: 'Error',
            responseMessage: error.message,
          });
          isValid = false;
          setSpinnerText(null);
        }
      } else {
        setSaIdNumberError('*INVALID ID NUMBER');
        setPassportNumberError('');
        isValid = false;
      }
    } else {
      setSaIdNumberError('');
      setPassportNumberError('');
    }

    if (!selectedOrigin) {
      setSelectedOriginError('*REQUIRED');
      isValid = false;
    } else {
      setSelectedOriginError('');
    }

    let genderID = '';

    if (saIdNumber) {
      const idGenderCheck = parseInt(saIdNumber.substring(6, 10));

      if (idGenderCheck >= 5000) {
        genderID = 'male';
      } else if (idGenderCheck <= 5000) {
        genderID = 'female';
      }

      if (selectedGender) {
        if (selectedGender.label === 'Not Disclosed') {
          setSexAtBirthError('');
        } else if (genderID.toLowerCase() === selectedGender.label.toString().toLowerCase()) {
          setSexAtBirthError('');
        } else {
          isValid = false;
          setSexAtBirthError('Does not match ID Gender.');
        }
      }
    }

    return isValid;
  };

  const handleSaveAndNextClick = async () => {
    const validated = await validateFields();

    if (!validated) {
      return;
    }

    const client = {
      title: title?.value ? title?.value : null,
      dob: dateOfBirth ? dateOfBirth : null,
      firstName: firstName ? firstName : '',
      lastName: lastName ? lastName : '',
      origin: selectedOrigin ? selectedOrigin : null,
      whatShouldWeCallYou: whatShouldWeCallYou ? whatShouldWeCallYou : null,
      passportNumber: passportNumber ? passportNumber : '',
      registrationDate: registrationDate ? registrationDate : null,
      saIdNumber: saIdNumber ? saIdNumber : '',
      sexAtBirth: selectedGender ? selectedGender : null,
    };

    setClient(client);

    setContext((prevState) => ({
      ...prevState,
      viewMode: false,
      breadcrumbs: {
        ...prevState.breadcrumbs,
        newClientGeneralBreadcrumb: {
          ...prevState.breadcrumbs.newClientGeneralBreadcrumb,
          type: 'General',
          screenKey: 'clientsCreateGeneral',
          data: client,
        },
      },
      editMode: true,
    }));

    handleSaveClick('clientsCreateConsent');
    setShowConsentTab(true);
  };

  const formatDateString = (inputDateString) => {
    const dateObject = new Date(inputDateString);
    if (isNaN(dateObject.getTime())) {
      return inputDateString;
    }

    const year = dateObject.getFullYear();
    const month = `0${dateObject.getMonth() + 1}`.slice(-2);
    const day = `0${dateObject.getDate()}`.slice(-2);

    return `${year}-${month}-${day}`;
  };

  const handleSaveChangesClick = async () => {
    const validated = await validateFields();

    if (!validated) {
      return;
    }

    const newClient = {
      ...client,
      title: title?.value ? title?.value : null,
      dob: dateOfBirth ? formatDateString(dateOfBirth) : null,
      firstName: firstName ? firstName : '',
      lastName: lastName ? lastName : '',
      origin: selectedOrigin ? selectedOrigin.label : null,
      whatShouldWeCallYou: whatShouldWeCallYou ? whatShouldWeCallYou : null,
      passportNumber: passportNumber ? passportNumber : null,
      registrationDate: registrationDate ? formatDateString(registrationDate) : null,
      saIdNumber: saIdNumber ? saIdNumber : null,
      sexAtBirth: selectedGender ? selectedGender.label : null,
    };
    setClient(newClient);
    handleUpdate(newClient);
  };

  const fieldsDisabled = !hasRoles(context, roles);

  return (
    <div className="tab-container">
      <div className="form-section">
        <div className="form-section-left">
          <div className="input-container">
            <label className={'input-label'}>Registration Date:</label>
            <DatePicker
              selected={registrationDate}
              className={`text-input ${registrationDateError && 'error-border'}`}
              dateFormat="dd-MM-yyyy"
              showPopperArrow={false}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              placeholderText="Select..."
              disabled={fieldsDisabled}
              onChange={(selectedDate) => setRegistrationDate(selectedDate)}
              popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
            />
            <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientRegistrationDate123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Registration Date:'}
              />
            )}
          </div>
          <div className="error-message">
            {registrationDateError && <div className="small-red-text">{registrationDateError}</div>}
          </div>
          <div className="input-container">
            <label className={'input-label'}>Title:</label>
            <Select
              options={titleList.current.map((selectedTitle, index) => ({
                value: selectedTitle,
                label: selectedTitle,
              }))}
              closeMenuOnSelect={true}
              onChange={setTitle}
              value={title || null}
              isDisabled={fieldsDisabled}
              classNamePrefix="input-select"
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientTitle123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Title:'}
              />
            )}
          </div>
          <div className="input-container">
            <label className={'input-label'}>First Name:</label>
            <input
              className={`text-input ${firstNameError && 'error-border'}`}
              type="text"
              value={firstName}
              disabled={fieldsDisabled}
              onChange={({ target }) => {
                setFirstName(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientFirstName123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'First Name:'}
              />
            )}
          </div>
          <div className="error-message">
            {firstNameError && <div className="small-red-text">{firstNameError}</div>}
          </div>

          <div className="input-container">
            <label className={'input-label'}>Last Name:</label>
            <input
              className={`text-input ${lastNameError && 'error-border'}`}
              type="text"
              value={lastName}
              disabled={fieldsDisabled}
              onChange={({ target }) => {
                setLastName(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientLastName123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Last Name:'}
              />
            )}
          </div>
          <div className="error-message">{lastNameError && <div className="small-red-text">{lastNameError}</div>}</div>

          <div className="input-container">
            <label className={'input-label'}>What should we call you?</label>
            <input
              className="text-input"
              type="text"
              value={whatShouldWeCallYou}
              disabled={fieldsDisabled}
              onChange={({ target }) => {
                setWhatShouldWeCallYou(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'whatShouldWeCallYou123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'What should we call you?'}
              />
            )}
          </div>

          <div className="input-container">
            <label className={'input-label'}>Client Origin:</label>
            <Select
              options={clientOriginsList.current.map((origin, index) => ({
                value: origin,
                label: origin,
              }))}
              closeMenuOnSelect={true}
              onChange={handleOriginChange}
              isDisabled={fieldsDisabled}
              value={selectedOrigin || null}
              classNamePrefix={`input-select${selectedOriginError && 'error-border'}`}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientOrigins123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Client Origins:'}
              />
            )}
          </div>
          <div className="error-message">
            {selectedOriginError && <div className="small-red-text">{selectedOriginError}</div>}
          </div>
        </div>

        <div className="form-section-right">
          <div className="input-container">
            <label className={'input-label-right'}>Sex at Birth:</label>
            <Select
              options={genderList.current.map((gender, index) => ({
                value: gender,
                label: gender,
              }))}
              closeMenuOnSelect={true}
              onChange={handleGenderChange}
              isDisabled={fieldsDisabled}
              value={selectedGender || null}
              classNamePrefix={`input-select${sexAtBirthError && 'error-border'}`}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientGender123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Sex at Birth:'}
              />
            )}
          </div>
          <div className="error-message">
            {sexAtBirthError && <div className="small-red-text">{sexAtBirthError}</div>}
          </div>

          <div className="input-container">
            <label className={'input-label-right'}>Date of Birth:</label>
            <DatePicker
              selected={dateOfBirth}
              className={`text-input ${dateOfBirthError && 'error-border'}`}
              dateFormat="dd-MM-yyyy"
              showPopperArrow={false}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              placeholderText="Select..."
              disabled={fieldsDisabled}
              onChange={(selectedDate) => setDateOfBirth(selectedDate)}
              popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
            />
            <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon-right" />

            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientDOB123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Date Of Birth:'}
              />
            )}
          </div>
          <div className="error-message-right">
            {dateOfBirthError && <div className="small-red-text">{dateOfBirthError}</div>}
          </div>

          <div className="input-container">
            <label className={'input-label-right'}>SA ID Number:</label>
            <input
              className={`text-input ${saIdNumberError && 'error-border'}`}
              type="text"
              value={saIdNumber}
              disabled={fieldsDisabled}
              onChange={({ target }) => {
                setSAIdNumber(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientSAIdNumber123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'SA Id Number:'}
              />
            )}
          </div>
          <div className="error-message-right">
            {saIdNumberError && <div className="small-red-text">{saIdNumberError}</div>}
          </div>

          <div className="input-container">
            <label className={'input-label-right'}>Passport Number:</label>
            <input
              className={`text-input ${passportNumberError && 'error-border'}`}
              type="text"
              value={passportNumber}
              disabled={fieldsDisabled}
              onChange={({ target }) => {
                setPassportNumber(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'clientPassportNumber123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Passport Number:'}
              />
            )}
          </div>
          <div className="error-message-right">
            {passportNumberError && <div className="small-red-text">{passportNumberError}</div>}
          </div>
        </div>
      </div>

      <div className={'button-container'}>
        {editMode && (
          <div className={'button-create-container'}>
            <SaveButton
              color={'#077187'}
              title={'Save Changes'}
              onClick={handleSaveChangesClick}
              disabled={fieldsDisabled}
            />
          </div>
        )}
        {!editMode && (
          <div className={'button-create-container'}>
            <SaveButton
              color={'#077187'}
              title={'Save and Next'}
              disabled={fieldsDisabled}
              onClick={handleSaveAndNextClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};
